/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { CommonModule } from "@angular/common";
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule, FormsModule } from "@angular/forms";
import { CardModule } from "primeng/card";
import { ButtonModule } from "primeng/button";
import { TooltipModule } from "primeng/tooltip";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ConfirmationService } from "primeng/api";
import { TextareaModule } from "primeng/textarea";
import {
  CustomerPortalSettingsForm,
  DetailedSummaryComponent,
  DetailedSummaryConfig,
} from "../../../interfaces/customer-portal-settings.interface";
import {
  UnifiedComponentItem,
  CustomComponent,
  CustomComponentFormData,
} from "../../../interfaces/custom-component.interface";
import { CustomComponentStorageService } from "../../../services/custom-component-storage.service";
import { CustomComponentEditorComponent } from "../custom-components/custom-component-editor/custom-component-editor.component";

@Component({
  selector: "app-advanced-configuration",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CardModule,
    ButtonModule,
    TooltipModule,
    TextareaModule,
    ConfirmDialogModule,
    CustomComponentEditorComponent,
  ],
  providers: [ConfirmationService],
  template: `
    <div class="advanced-config-container">
      <!-- Main Content Row: Portal Components + CSS Cards -->
      <div class="main-content-row">
        <!-- Unified Components Card -->
        <p-card class="portal-components-card">
          <ng-template pTemplate="title">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <i class="pi pi-list text-indigo-600"></i>
                Portal Components
                <i
                  class="pi pi-info-circle text-gray-400 cursor-pointer"
                  pTooltip="Configure which components appear and their order in the detailed summary view. Drag to reorder."
                  tooltipPosition="right"
                  [style.font-size]="'0.875rem'"
                ></i>
              </div>
              <p-button
                label="Add Custom Component"
                icon="pi pi-plus"
                [outlined]="true"
                severity="success"
                size="small"
                (click)="openCustomEditor()"
                styleClass="add-component-btn"
              />
            </div>
          </ng-template>
          <ng-template pTemplate="subtitle">
            Control which information appears in detailed views and organize
            their display order. Includes both standard and custom components.
          </ng-template>
          <ng-template pTemplate="content">
            <div class="components-header">
              <div class="components-info">
                <i class="pi pi-info-circle text-blue-500"></i>
                <span>Drag to reorder • Click eye to show/hide</span>
              </div>
              <div class="components-count">
                {{ getEnabledComponentsCount() }}/{{ unifiedComponents.length }}
                enabled
              </div>
            </div>

            <div class="component-grid">
              @for (
                component of unifiedComponents;
                track component.identifier;
                let i = $index
              ) {
                <div
                  class="component-card"
                  [class.component-card-disabled]="!component.enabled"
                  [class.component-card-custom]="component.isCustom"
                  [class.component-card-dragging]="
                    isDragging && draggedIndex === i
                  "
                  draggable="true"
                  (dragstart)="onDragStart($event, i)"
                  (dragover)="onDragOver($event)"
                  (drop)="onDrop($event, i)"
                  (dragend)="onDragEnd()"
                >
                  <div class="component-inline-row">
                    <div
                      class="component-order-badge"
                      [class.component-order-badge-custom]="component.isCustom"
                    >
                      {{ component.order + 1 }}
                    </div>
                    <i
                      class="drag-handle pi pi-grip-vertical"
                      title="Drag to reorder"
                      pTooltip="Drag to change display order"
                      tooltipPosition="top"
                    ></i>
                    <i
                      class="component-status-icon pi cursor-pointer"
                      [class.pi-eye]="component.enabled"
                      [class.pi-eye-slash]="!component.enabled"
                      [style.color]="component.enabled ? '#10b981' : '#ef4444'"
                      [title]="
                        component.enabled ? 'Hide component' : 'Show component'
                      "
                      (click)="onComponentToggle(i, !component.enabled)"
                    ></i>
                    <div class="component-info">
                      <span class="component-title">{{ component.label }}</span>
                      @if (component.isCustom) {
                        <span class="custom-badge">CUSTOM</span>
                      }
                    </div>
                    <div class="component-actions">
                      @if (component.isCustom) {
                        <p-button
                          icon="pi pi-pencil"
                          [text]="true"
                          size="small"
                          severity="info"
                          pTooltip="Edit component"
                          tooltipPosition="top"
                          (click)="editCustomComponent(component)"
                        />
                        <p-button
                          icon="pi pi-trash"
                          [text]="true"
                          size="small"
                          severity="danger"
                          pTooltip="Delete component"
                          tooltipPosition="top"
                          (click)="confirmDeleteCustomComponent(component)"
                        />
                      }
                      @if (i > 0) {
                        <p-button
                          icon="pi pi-angle-up"
                          [text]="true"
                          size="small"
                          severity="secondary"
                          pTooltip="Move up"
                          tooltipPosition="top"
                          (click)="moveComponent(i, -1)"
                        />
                      }
                      @if (i < unifiedComponents.length - 1) {
                        <p-button
                          icon="pi pi-angle-down"
                          [text]="true"
                          size="small"
                          severity="secondary"
                          pTooltip="Move down"
                          tooltipPosition="top"
                          (click)="moveComponent(i, 1)"
                        />
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          </ng-template>
        </p-card>

        <!-- CSS Cards Sidebar -->
        <div class="css-sidebar">
          <!-- Custom CSS for Components -->
          <p-card class="css-card">
            <ng-template pTemplate="title">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <i class="pi pi-code text-cyan-600"></i>
                  Global Custom Component Styles (CSS)
                  <i
                    class="pi pi-info-circle text-gray-400 cursor-pointer"
                    pTooltip="Add global custom CSS styles for detailed summary components"
                    tooltipPosition="right"
                    [style.font-size]="'0.875rem'"
                  ></i>
                </div>
                <p-button
                  [icon]="
                    isComponentCssExpanded ? 'pi pi-angle-up' : 'pi pi-pencil'
                  "
                  [severity]="isComponentCssExpanded ? 'secondary' : 'info'"
                  [text]="true"
                  size="small"
                  [pTooltip]="
                    isComponentCssExpanded
                      ? 'Collapse CSS editor'
                      : 'Edit custom CSS'
                  "
                  tooltipPosition="left"
                  (click)="toggleComponentCssEditor()"
                />
              </div>
            </ng-template>
            <ng-template pTemplate="subtitle">
              Add global custom CSS styles for the detailed summary components.
            </ng-template>
            <ng-template pTemplate="content">
              @if (!isComponentCssExpanded) {
                <div class="text-sm text-gray-600 py-2">
                  <div class="flex items-center gap-2">
                    <i class="pi pi-info-circle"></i>
                    <span
                      >Click the edit button to customize component styles</span
                    >
                  </div>
                  @if (hasComponentCustomCss()) {
                    <div class="mt-2 text-xs text-green-600">
                      ✓ Global custom styles applied ({{
                        getComponentCssLineCount()
                      }}
                      {{ getComponentCssLineCount() === 1 ? "line" : "lines" }})
                    </div>
                  }
                </div>
              }
              @if (isComponentCssExpanded) {
                <div class="field">
                  <textarea
                    pInputTextarea
                    [formControl]="formGroup.controls.detailedSummaryCustomCss"
                    rows="15"
                    placeholder="/* Add your custom CSS here */&#10;.component-class {&#10;  /* Your styles */&#10;}"
                    class="w-full font-mono text-sm"
                  ></textarea>
                </div>
              }
            </ng-template>
          </p-card>

          <!-- Global Custom CSS -->
          <p-card class="css-card">
            <ng-template pTemplate="title">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <i class="pi pi-globe text-purple-600"></i>
                  Global Custom Styles (CSS)
                  <i
                    class="pi pi-info-circle text-gray-400 cursor-pointer"
                    pTooltip="Add custom CSS styles that apply globally to your customer portal"
                    tooltipPosition="right"
                    [style.font-size]="'0.875rem'"
                  ></i>
                </div>
                <p-button
                  [icon]="
                    isGlobalCssExpanded ? 'pi pi-angle-up' : 'pi pi-pencil'
                  "
                  [severity]="isGlobalCssExpanded ? 'secondary' : 'info'"
                  [text]="true"
                  size="small"
                  [pTooltip]="
                    isGlobalCssExpanded
                      ? 'Collapse CSS editor'
                      : 'Edit global CSS'
                  "
                  tooltipPosition="left"
                  (click)="toggleGlobalCssEditor()"
                />
              </div>
            </ng-template>
            <ng-template pTemplate="subtitle">
              Add custom CSS styles that apply globally to your customer portal.
            </ng-template>
            <ng-template pTemplate="content">
              @if (!isGlobalCssExpanded) {
                <div class="text-sm text-gray-600 py-2">
                  <div class="flex items-center gap-2">
                    <i class="pi pi-info-circle"></i>
                    <span
                      >Click the edit button to customize global styles</span
                    >
                  </div>
                  @if (hasGlobalCustomCss()) {
                    <div class="mt-2 text-xs text-green-600">
                      ✓ Global custom styles applied ({{
                        getGlobalCssLineCount()
                      }}
                      {{ getGlobalCssLineCount() === 1 ? "line" : "lines" }})
                    </div>
                  }
                </div>
              }
              @if (isGlobalCssExpanded) {
                <div class="field">
                  <textarea
                    pInputTextarea
                    [formControl]="formGroup.controls.customStyles"
                    rows="25"
                    placeholder="/* Add your global CSS here */&#10;.portal-wrapper {&#10;  /* Global styles */&#10;}&#10;&#10;/* Responsive styles */&#10;@media (max-width: 768px) {&#10;  .mobile-specific {&#10;    /* Mobile styles */&#10;  }&#10;}"
                    class="w-full font-mono text-sm"
                  ></textarea>
                </div>
              }
            </ng-template>
          </p-card>
        </div>
      </div>
    </div>

    <!-- Custom Component Editor Dialog -->
    <app-custom-component-editor
      [(visible)]="editorVisible"
      [component]="selectedCustomComponent"
      [existingComponents]="detailedSummaryComponents"
      [customComponents]="customComponents"
      (save)="onEditorSave($event)"
      (cancelled)="onEditorCancel()"
    />

    <!-- Confirm Dialog -->
    <p-confirmDialog />
  `,
  styles: [
    `
      :host {
        display: block;
        height: fit-content;
        min-height: auto;
      }

      .advanced-config-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }

      /* Desktop: Two-column layout */
      .main-content-row {
        display: flex;
        gap: 1.5rem;
        align-items: flex-start;
      }

      .portal-components-card {
        flex: 1;
        min-width: 0;
      }

      .css-sidebar {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        width: 320px;
        flex-shrink: 0;
      }

      .css-card {
        width: 100%;
      }

      .components-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
        padding: 0.75rem;
        background: #f8fafc;
        border-radius: 6px;
        border: 1px solid #e2e8f0;
      }

      .components-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: #64748b;
      }

      .components-count {
        font-size: 0.75rem;
        font-weight: 600;
        color: #475569;
        background: #e2e8f0;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
      }

      .component-grid {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
        margin-top: 0.5rem;
      }

      .component-inline-row {
        display: flex;
        align-items: center;
        padding: 0.375rem 0.75rem;
        gap: 0.5rem;
        width: 100%;
      }

      .component-card {
        background: linear-gradient(135deg, #ffffff 0%, #fdfdfd 100%);
        border: 1px solid rgba(226, 232, 240, 0.8);
        border-radius: 8px;
        overflow: hidden;
        cursor: move;
        position: relative;
        box-shadow:
          0 1px 2px 0 rgba(0, 0, 0, 0.05),
          0 1px 1px 0 rgba(0, 0, 0, 0.04);
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

        &::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.6),
            transparent
          );
          z-index: 1;
        }
      }

      .component-card:hover:not(.component-card-disabled) {
        transform: translateY(-1px);
        border-color: rgba(59, 130, 246, 0.3);
        box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.08);
        background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);

        .component-order-badge {
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          color: #1d4ed8;
          border-color: #93c5fd;
        }
      }

      /* Custom component styling */
      .component-card-custom {
        background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
        border-color: #bbf7d0;

        &:hover:not(.component-card-disabled) {
          border-color: #22c55e;
          box-shadow: 0 2px 4px -1px rgba(34, 197, 94, 0.1);
          background: linear-gradient(135deg, #ffffff 0%, #ecfdf5 100%);

          .component-order-badge-custom {
            background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
            color: #15803d;
            border-color: #86efac;
          }
        }
      }

      .component-card-disabled {
        opacity: 0.6;
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border-color: #e2e8f0;
        cursor: not-allowed;

        .component-order-badge {
          background: #f8fafc;
          color: #94a3b8;
          border-color: #e2e8f0;
        }

        .component-title {
          color: #9ca3af;
        }

        .drag-handle {
          color: #cbd5e1;
          cursor: not-allowed;
        }
      }

      .component-card-dragging {
        transform: rotate(3deg) scale(1.05);
        z-index: 1000;
        box-shadow:
          0 10px 25px -5px rgba(0, 0, 0, 0.25),
          0 20px 40px -10px rgba(59, 130, 246, 0.3),
          0 0 0 2px rgba(59, 130, 246, 0.4);
        animation: dragPulse 2s infinite;
      }

      @keyframes dragPulse {
        0%,
        100% {
          box-shadow:
            0 10px 25px -5px rgba(0, 0, 0, 0.25),
            0 20px 40px -10px rgba(59, 130, 246, 0.3),
            0 0 0 2px rgba(59, 130, 246, 0.4);
        }
        50% {
          box-shadow:
            0 15px 35px -5px rgba(0, 0, 0, 0.3),
            0 25px 50px -10px rgba(59, 130, 246, 0.4),
            0 0 0 3px rgba(59, 130, 246, 0.6);
        }
      }

      .drag-handle {
        color: #9ca3af;
        cursor: grab;
        font-size: 0.75rem;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .drag-handle:hover {
        color: #374151;
        background: rgba(59, 130, 246, 0.1);
        border-color: rgba(59, 130, 246, 0.3);
        transform: scale(1.1);
      }

      .drag-handle:active {
        cursor: grabbing;
        color: #1f2937;
        background: rgba(59, 130, 246, 0.2);
        border-color: rgba(59, 130, 246, 0.5);
        transform: scale(0.95);
      }

      .component-order-badge {
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        color: #3b82f6;
        border-radius: 6px;
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.75rem;
        font-weight: 600;
        transition: all 0.2s ease;
        border: 1px solid #dbeafe;
        flex-shrink: 0;
        font-family: "Inter", sans-serif;
      }

      .component-order-badge-custom {
        background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
        color: #059669;
        border-color: #a7f3d0;
      }

      .component-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex: 1;
        min-width: 0;
      }

      .component-title {
        cursor: pointer;
        font-weight: 500;
        color: #1f2937;
        font-size: 0.8125rem;
        line-height: 1.3;
        letter-spacing: -0.01em;
        transition: color 0.2s ease;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .component-card:not(.component-card-disabled) .component-title:hover {
        color: #3b82f6;
      }

      .custom-badge {
        font-size: 0.5625rem;
        font-weight: 700;
        text-transform: uppercase;
        color: #059669;
        background: #d1fae5;
        padding: 0.125rem 0.375rem;
        border-radius: 4px;
        flex-shrink: 0;
        letter-spacing: 0.025em;
      }

      .component-card-disabled .custom-badge {
        color: #94a3b8;
        background: #f1f5f9;
      }

      .component-actions {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        opacity: 0.7;
        transition: opacity 0.2s ease;
        flex-shrink: 0;
        margin-left: auto;
      }

      .component-card:hover .component-actions {
        opacity: 1;
      }

      .component-status-icon {
        margin-right: 0.375rem;
        font-size: 0.875rem;
        transition: all 0.2s ease;
        flex-shrink: 0;
        padding: 0.25rem;
        border-radius: 4px;
      }

      .component-status-icon:hover {
        background: rgba(0, 0, 0, 0.05);
        transform: scale(1.1);
      }

      @media (max-width: 1024px) {
        .main-content-row {
          flex-direction: column;
        }

        .css-sidebar {
          width: 100%;
          flex-shrink: 1;
        }

        .components-header {
          flex-direction: column;
          gap: 0.5rem;
          align-items: flex-start;
        }
      }

      /* Mobile responsive styles */
      @media (max-width: 768px) {
        /* Card title row - stack on mobile */
        :host
          ::ng-deep
          .p-card
          .p-card-title
          .flex.items-center.justify-between {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.75rem;
        }

        /* Add Component button - compact on mobile */
        :host ::ng-deep .add-component-btn {
          font-size: 0.6875rem !important;
          padding: 0.375rem 0.625rem !important;
          white-space: nowrap;
        }

        :host ::ng-deep .add-component-btn .p-button-label {
          font-size: 0.6875rem !important;
        }

        :host ::ng-deep .add-component-btn .p-button-icon {
          font-size: 0.6875rem !important;
        }

        /* Component card - allow 2 lines for name */
        .component-inline-row {
          flex-wrap: wrap;
          padding: 0.5rem;
          gap: 0.375rem;
        }

        .component-info {
          flex: 1 1 auto;
          min-width: 0;
          max-width: calc(100% - 100px);
        }

        .component-title {
          white-space: normal;
          overflow: visible;
          text-overflow: unset;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          line-height: 1.4;
          font-size: 0.75rem;
        }

        .custom-badge {
          font-size: 0.5rem;
          padding: 0.0625rem 0.25rem;
        }

        .component-order-badge {
          width: 20px;
          height: 20px;
          font-size: 0.6875rem;
        }

        .drag-handle {
          font-size: 0.6875rem;
        }

        .component-status-icon {
          font-size: 0.75rem;
          margin-right: 0.25rem;
        }

        /* Actions - always visible, smaller */
        .component-actions {
          opacity: 1;
          gap: 0.125rem;
        }

        :host ::ng-deep .component-actions .p-button-sm {
          font-size: 0.5625rem;
          padding: 0.1875rem 0.25rem;
        }

        :host ::ng-deep .component-actions .p-button-sm .p-button-icon {
          font-size: 0.625rem;
        }

        /* Components header */
        .components-header {
          padding: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .components-info {
          font-size: 0.75rem;
        }

        .components-count {
          font-size: 0.6875rem;
        }
      }

      /* Extra small screens */
      @media (max-width: 480px) {
        .component-inline-row {
          padding: 0.375rem;
        }

        .component-info {
          max-width: calc(100% - 80px);
        }

        .component-title {
          font-size: 0.6875rem;
        }

        .component-order-badge {
          width: 18px;
          height: 18px;
          font-size: 0.625rem;
        }

        :host ::ng-deep .component-actions .p-button-sm {
          padding: 0.125rem 0.1875rem;
        }

        :host ::ng-deep .component-actions .p-button-sm .p-button-icon {
          font-size: 0.5625rem;
        }
      }

      .field {
        width: 100%;
      }

      :host ::ng-deep textarea {
        font-family: "JetBrains Mono", "Fira Code", "Courier New", monospace;
        font-size: 0.875rem;
        line-height: 1.5;
        border-radius: 8px;
        border: 1px solid #d1d5db;
        transition: border-color 0.2s ease;
      }

      :host ::ng-deep textarea:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      :host ::ng-deep .p-card {
        height: fit-content;
        width: 100%;
        box-shadow:
          0 1px 3px 0 rgb(0 0 0 / 0.1),
          0 1px 2px -1px rgb(0 0 0 / 0.1);
        border: 1px solid #e5e7eb;
        border-radius: 8px;
      }

      :host ::ng-deep .p-card .p-card-header {
        border-bottom: 1px solid #e5e7eb;
        padding: 1.25rem;
      }

      :host ::ng-deep .p-card .p-card-content {
        padding: 0;
      }

      :host ::ng-deep .p-card .p-card-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
      }

      :host ::ng-deep .p-card .p-card-subtitle {
        color: #6b7280;
        font-size: 0.875rem;
        margin-top: 0.25rem;
      }

      :host ::ng-deep .p-button-sm {
        font-size: 0.6875rem;
        padding: 0.25rem 0.375rem;
      }

      :host ::ng-deep .p-button-sm .p-button-icon {
        font-size: 0.6875rem;
      }
    `,
  ],
})
export class AdvancedConfigurationComponent implements OnInit, OnChanges {
  private readonly storageService = inject(CustomComponentStorageService);
  private readonly confirmationService = inject(ConfirmationService);

  @Input({ required: true }) formGroup!: FormGroup<CustomerPortalSettingsForm>;
  @Input({ required: true }) isLoading!: boolean;
  @Input({ required: true })
  detailedSummaryComponents!: DetailedSummaryComponent[];
  @Input({ required: true }) siteId!: number;
  @Input() detailedSummaryConfig: DetailedSummaryConfig | null | undefined =
    null;

  @Output() componentConfigChange = new EventEmitter<void>();
  @Output() customComponentsChange = new EventEmitter<CustomComponent[]>();
  @Output() unifiedOrderChange = new EventEmitter<UnifiedComponentItem[]>();

  // Unified components list
  unifiedComponents: UnifiedComponentItem[] = [];
  customComponents: CustomComponent[] = [];

  // Drag state
  draggedIndex: number = -1;
  isDragging = false;

  // CSS editor state
  isComponentCssExpanded = false;
  isGlobalCssExpanded = false;

  // Custom component editor state
  editorVisible = false;
  selectedCustomComponent: CustomComponent | null = null;

  ngOnInit(): void {
    this.loadComponentsFromConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only re-initialize on first change (initial API load), not on subsequent mutations
    // This prevents toggling from being reverted when detailedSummaryComponents is mutated
    const configChange = changes["detailedSummaryConfig"];
    const componentsChange = changes["detailedSummaryComponents"];

    if (
      (configChange?.firstChange && configChange.currentValue) ||
      (componentsChange?.firstChange && componentsChange.currentValue)
    ) {
      this.loadComponentsFromConfig();
    }
  }

  private loadComponentsFromConfig(): void {
    // Extract custom components from API config
    this.customComponents = this.storageService.getCustomComponentsFromConfig(
      this.detailedSummaryConfig,
    );

    // Get existing unified order from API config
    const existingUnifiedOrder = this.storageService.getUnifiedOrderFromConfig(
      this.detailedSummaryConfig,
      this.customComponents,
    );

    // Initialize/sync unified order
    this.unifiedComponents = this.storageService.initializeUnifiedOrder(
      this.detailedSummaryComponents,
      this.customComponents,
      existingUnifiedOrder,
    );
  }

  onComponentToggle(index: number, enabled: boolean): void {
    this.unifiedComponents[index].enabled = enabled;

    // Update the source data
    const component = this.unifiedComponents[index];
    if (component.isCustom) {
      // Update custom component in local array
      const result = this.storageService.updateComponentInArray(
        this.customComponents,
        component.identifier,
        { enabled },
      );
      this.customComponents = result.components;

      // Update customData reference in unified order
      if (result.updated) {
        component.customData = result.updated;
      }

      this.customComponentsChange.emit(this.customComponents);
    } else {
      // Update standard component
      const standardComp = this.detailedSummaryComponents.find(
        (c) => c.Identifier === component.identifier,
      );
      if (standardComp) {
        standardComp.Enabled = enabled;
      }
      this.componentConfigChange.emit();
    }

    this.unifiedOrderChange.emit(this.unifiedComponents);
  }

  onDragStart(event: DragEvent, index: number): void {
    this.draggedIndex = index;
    this.isDragging = true;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/html", "");
    }
  }

  onDragEnd(): void {
    this.isDragging = false;
    this.draggedIndex = -1;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  onDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();
    this.isDragging = false;

    if (this.draggedIndex !== dropIndex && this.draggedIndex !== -1) {
      // Remove dragged item and insert at new position
      const draggedItem = this.unifiedComponents[this.draggedIndex];
      this.unifiedComponents.splice(this.draggedIndex, 1);
      this.unifiedComponents.splice(dropIndex, 0, draggedItem);

      // Update order values
      this.unifiedComponents.forEach((component, idx) => {
        component.order = idx;
      });

      // Sync back to standard components
      this.syncToStandardComponents();

      // Sync custom component orders
      this.syncCustomComponentOrders();

      this.componentConfigChange.emit();
      this.customComponentsChange.emit(this.customComponents);
      this.unifiedOrderChange.emit(this.unifiedComponents);
    }

    this.draggedIndex = -1;
  }

  moveComponent(index: number, direction: number): void {
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < this.unifiedComponents.length) {
      // Swap components
      const temp = this.unifiedComponents[index];
      this.unifiedComponents[index] = this.unifiedComponents[newIndex];
      this.unifiedComponents[newIndex] = temp;

      // Update order values
      this.unifiedComponents.forEach((component, idx) => {
        component.order = idx;
      });

      // Sync back to standard components
      this.syncToStandardComponents();

      // Sync custom component orders
      this.syncCustomComponentOrders();

      this.componentConfigChange.emit();
      this.customComponentsChange.emit(this.customComponents);
      this.unifiedOrderChange.emit(this.unifiedComponents);
    }
  }

  private syncToStandardComponents(): void {
    // Update the detailedSummaryComponents with new order and enabled state
    this.unifiedComponents.forEach((unified) => {
      if (!unified.isCustom) {
        const standardComp = this.detailedSummaryComponents.find(
          (c) => c.Identifier === unified.identifier,
        );
        if (standardComp) {
          standardComp.Order = unified.order;
          standardComp.Enabled = unified.enabled;
        }
      }
    });
  }

  private syncCustomComponentOrders(): void {
    // Update custom components with new order values from unified order
    this.unifiedComponents.forEach((unified) => {
      if (unified.isCustom) {
        const customComp = this.customComponents.find(
          (c) => c.id === unified.identifier,
        );
        if (customComp) {
          customComp.order = unified.order;
          unified.customData = customComp;
        }
      }
    });
  }

  getEnabledComponentsCount(): number {
    return this.unifiedComponents.filter((comp) => comp.enabled).length;
  }

  // Custom component methods
  openCustomEditor(component?: CustomComponent): void {
    this.selectedCustomComponent = component || null;
    this.editorVisible = true;
  }

  editCustomComponent(item: UnifiedComponentItem): void {
    if (item.customData) {
      this.openCustomEditor(item.customData);
    }
  }

  confirmDeleteCustomComponent(item: UnifiedComponentItem): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${item.label}"?`,
      header: "Delete Custom Component",
      icon: "pi pi-exclamation-triangle",
      acceptButtonStyleClass: "p-button-danger",
      accept: () => {
        this.deleteCustomComponent(item);
      },
    });
  }

  deleteCustomComponent(item: UnifiedComponentItem): void {
    // Remove from custom components array
    this.customComponents = this.storageService.deleteComponentFromArray(
      this.customComponents,
      item.identifier,
    );

    // Remove from unified order
    this.unifiedComponents =
      this.storageService.removeComponentFromUnifiedOrder(
        item.identifier,
        this.unifiedComponents,
      );

    this.customComponentsChange.emit(this.customComponents);
    this.unifiedOrderChange.emit(this.unifiedComponents);
  }

  onEditorSave(formData: CustomComponentFormData): void {
    if (this.selectedCustomComponent) {
      // Update existing
      const result = this.storageService.updateComponentInArray(
        this.customComponents,
        this.selectedCustomComponent.id,
        formData,
      );

      this.customComponents = result.components;

      if (result.updated) {
        // Update in unified order
        this.unifiedComponents =
          this.storageService.updateComponentInUnifiedOrder(
            this.selectedCustomComponent.id,
            {
              label: formData.name,
              enabled: formData.enabled,
              customData: result.updated,
            },
            this.unifiedComponents,
          );
      }
    } else {
      // Create new
      const newComponent = this.storageService.createComponent({
        ...formData,
        order: this.storageService.getNextOrder(this.unifiedComponents),
      });

      this.customComponents = [...this.customComponents, newComponent];

      this.unifiedComponents = this.storageService.addComponentToUnifiedOrder(
        newComponent,
        this.unifiedComponents,
      );
    }

    this.customComponentsChange.emit(this.customComponents);
    this.unifiedOrderChange.emit(this.unifiedComponents);
    this.editorVisible = false;
    this.selectedCustomComponent = null;
  }

  onEditorCancel(): void {
    this.editorVisible = false;
    this.selectedCustomComponent = null;
  }

  // CSS editor methods
  toggleComponentCssEditor(): void {
    this.isComponentCssExpanded = !this.isComponentCssExpanded;
  }

  toggleGlobalCssEditor(): void {
    this.isGlobalCssExpanded = !this.isGlobalCssExpanded;
  }

  hasComponentCustomCss(): boolean {
    const css = this.formGroup.get("detailedSummaryCustomCss")?.value;
    return !!(css && typeof css === "string" && css.trim().length > 0);
  }

  hasGlobalCustomCss(): boolean {
    const css = this.formGroup.get("customStyles")?.value;
    return !!(css && typeof css === "string" && css.trim().length > 0);
  }

  getComponentCssLineCount(): number {
    const css = this.formGroup.get("detailedSummaryCustomCss")?.value;
    return css && typeof css === "string" ? css.split("\n").length : 0;
  }

  getGlobalCssLineCount(): number {
    const css = this.formGroup.get("customStyles")?.value;
    return css && typeof css === "string" ? css.split("\n").length : 0;
  }
}
